import {
  autoSignIn,
  signIn,
  signOut,
  signUp,
  confirmSignUp,
  resendSignUpCode,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  resetPassword,
  confirmResetPassword,
  signInWithRedirect,
  updateUserAttributes,
  confirmUserAttribute,
  sendUserAttributeVerificationCode,
  updatePassword
} from 'aws-amplify/auth';
import { backendConnector } from '../backendIntegration/lambdas';
import logger from '../utils/logger';
import { normalizeEmail, withLegacyEmailRetry, isSignInMiss, isConfirmMiss } from './authEmail';
import { markSignedOut } from './sessionEnded';

// Cognito reports this as NotAuthorizedException, the same error as a wrong code — the
// message is the only thing that tells them apart.
export const isAlreadyConfirmed = (error) =>
  /status is CONFIRMED/i.test(error?.message ?? '');

// A refusal (an unknown referrer, an account too old for a referral) will not change on retry, so
// it settles the referral as surely as a success. A 401, 408, 429, a 5xx or no answer at all might.
export const isReferralSettled = (error) =>
  error?.status >= 400 && error?.status < 500 && ![401, 408, 429].includes(error?.status);


class Authenticator {
  constructor() {
    if (Authenticator.instance) {
      return Authenticator.instance;
    }
    Authenticator.instance = this;
  }

  async getCurrentUser() {
    try {
      return await getCurrentUser();
    } catch (error) {
      logger.error('Error getting current user:', error);
    }
  }

  async getUserAttributes() {
    try {
      logger.log('Fetching attributes');
      return await fetchUserAttributes();
    } catch (error) {
      logger.error('Error fetching user attributes:', error);
    }
  }

  async getUserSessionDetails() {
    try {
      const session = await fetchAuthSession();

      // Ensure we have credentials
      if (!session.credentials) {
        throw new Error('No credentials in session');
      }

      // Get the identity ID
      const identityId = session.identityId;

      if (!identityId) {
        throw new Error('No identity ID found in session');
      }

      return {
        ...session,
        identityId
      };
    } catch (error) {
      logger.error('Error fetching auth session:', error);
      throw error;
    }
  }

  async handleSignOut() {
    try {
      // Clear localStorage
      localStorage.clear();
      // Clear sessionStorage
      sessionStorage.clear();
      // After the clear, so it survives: a reload of a protected page then skips the grace wait.
      markSignedOut();
      await signOut({ global: true });
    } catch (error) {
      logger.error('Error signing out:', error);
      return error.message;
    }
  }

  // Every call that takes an email normalizes it here (see authEmail.js); callers pass what the
  // user typed.
  async handleSignUp({ username, password, name }) {
    try {
      const user = await signUp({
        username: normalizeEmail(username),
        password,
        options: {
          userAttributes: { name: String(name ?? '').trim() },
          autoSignIn: true
        },
      });
      return user;
    } catch (error) {
      logger.error('Error signing up:', error);
      return error;
    }
  }

  async processReferral() {
    try {
      const attributes = await this.getUserAttributes();
      logger.log('User attributes:', attributes);
      const referralProcessed = attributes['custom:referralProcessed'];

      if (!referralProcessed) {
        logger.log('Processing referral...');
        // Marked processed only once the server has it. Marking it in parallel lost the referral,
        // and the signup's attribution, whenever the call failed: it was never sent again.
        try {
          await backendConnector.addReferral();
        } catch (error) {
          if (!isReferralSettled(error)) throw error;
        }
        await this.updateUserAttributes({ 'custom:referralProcessed': 'true' });
      }
    } catch (error) {
      logger.error('Error processing referral:', error);
    }
  }

  async isFirstLogin() {
    try {
      const attributes = await this.getUserAttributes();
      const isFirstLogin = attributes['custom:firstLogin'];
      if (isFirstLogin === "true") {
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  }

  async setFirstLoginFalse() {
    try {
      if (this.isFirstLogin()) {
        await this.updateUserAttributes({ 'custom:firstLogin': 'false' });
      }
    } catch (error) {
      logger.error('Error setting firstLogin state to false:', error);
    }
  }


  async handleSignUpConfirmation({ username, confirmationCode }) {
    try {
      const { result } = await withLegacyEmailRetry(
        username,
        (name) => confirmSignUp({ username: name, confirmationCode }),
        isConfirmMiss
      );
      return result;
    } catch (error) {
      // ConfirmSignUp isn't idempotent, but the SDK retries it on transient network
      // failures. If the first attempt lands and its response is lost, the retry returns
      // "User cannot be confirmed. Current status is CONFIRMED" — the account IS verified,
      // so reporting failure strands the user on the code screen with no way out. Safe to
      // treat as success: confirming grants no session, the sign-in below still gates.
      if (isAlreadyConfirmed(error)) {
        logger.log('Account already CONFIRMED — treating confirmation as success');
        return { isSignUpComplete: true, nextStep: { signUpStep: 'DONE' } };
      }
      logger.error('Error confirming sign up:', error);
      return error;
    }
  }


  async handleAutoSignIn() {
    try {
      return await autoSignIn();
    } catch (error) {
      logger.error('Error with auto sign-in:', error);
      return error.message;
    }
  }

  // Resolves to Amplify's result plus `username`: the spelling Cognito answered to, which is the
  // typed one only for a mixed-case account made before emails were normalized.
  async handleSignIn({ username, password }) {
    try {
      const { result, username: matched } = await withLegacyEmailRetry(
        username,
        (name) => signIn({ username: name, password }),
        isSignInMiss
      );
      logger.log('Signed in:', result);
      return { ...result, username: matched };
    } catch (error) {
      logger.error('Error signing in:', error);
      throw error;
    }
  }

  async resendSignUpInfo({ username }) {
    try {
      // The code screen passes the address Cognito knows (see handleSignIn), so send exactly that
      // when it is a legacy mixed-case account; otherwise the normalized address.
      await resendSignUpCode({ username: String(username ?? '').trim() });
    } catch (error) {
      logger.error('Error resending registration code:', error);
      throw error;
    }
  }

  async clearCache() {
    try {
      // Clear localStorage
      localStorage.clear();
      // Clear sessionStorage
      sessionStorage.clear();
      logger.log('Cache cleared successfully.');
    } catch (error) {
      logger.error('Error clearing cache:', error);
    }
  }


  // Both reset calls return the error (not its message) so the caller can choose the copy; the
  // first must never reveal whether an account exists.
  async handleForgotPassword({ username }) {
    try {
      await resetPassword({ username: normalizeEmail(username) });
      logger.log('Password reset initiated. Check your email for the confirmation code.');
    } catch (error) {
      logger.error('Error initiating password reset:', error);
      return error;
    }
  }

  async handleChangePassword({ oldPassword, newPassword }) {
    try {
      await updatePassword({ oldPassword, newPassword });
      logger.log('Password changed successfully.');
      return 'Password changed successfully.';
    } catch (error) {
      logger.error('Error changing password:', error);
      // Rethrown as is: the caller maps error.name to plain copy.
      throw error;
    }
  }

  async handleForgotPasswordSubmit({ username, confirmationCode, newPassword }) {
    try {
      await confirmResetPassword({ username: normalizeEmail(username), confirmationCode, newPassword });
      logger.log('Password reset successfully.');
    } catch (error) {
      logger.error('Error resetting password:', error);
      return error;
    }
  }

  async updateUserAttributes(attributes) {
    try {
      const currentUser = await this.getCurrentUser();
      if (!currentUser) {
        throw new Error('No user is currently signed in.');
      }

      // A changed sign-in email follows the same rule as a new account's.
      const userAttributes = 'email' in attributes
        ? { ...attributes, email: normalizeEmail(attributes.email) }
        : attributes;
      const input = { userAttributes };

      logger.log('Updating user attributes with input:', input);

      await updateUserAttributes(input);

      logger.log('User attributes updated successfully.');
      return 'User attributes updated successfully.';
    } catch (error) {
      logger.error('Error updating user attributes:', error);
      // Rethrown as is, so the caller can tell AliasExistsException from a network error.
      throw error;
    }
  }


  async signInWithGoogle() {
    try {
      logger.log('Signing in with Google...');
      await signInWithRedirect({ provider: 'Google' });
    } catch (error) {
      logger.error('Error signing in with Google:', error);
      throw error;
    }
  }

  async signInWithApple() {
    try {
      logger.log('Signing in with Apple...');
      await signInWithRedirect({ provider: 'Apple' });
    } catch (error) {
      logger.error('Error signing in with Apple:', error);
      throw error;
    }
  }

  async sendUserAttributeVerificationCode({ userAttributeKey }) {
    try {
      await sendUserAttributeVerificationCode({ userAttributeKey });
    } catch (error) {
      logger.error('Error sending attribute verification code:', error);
      throw error;
    }
  }

  async confirmUserAttribute({ userAttributeKey, confirmationCode }) {
    try {
      await confirmUserAttribute({ userAttributeKey, confirmationCode });
      return 'Attribute verified successfully.';
    } catch (error) {
      logger.error('Error confirming attribute:', error);
      throw error;
    }
  }

}


// Exporting the singleton instance
export const authenticator = new Authenticator();
